// src/plugins/sales-forecast/components/ConstructionPipelineChart.tsx
import React, { useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
} from "recharts";
import {
  ConstructionPhaseControl,
  type ConstructionPhase,
} from "./ConstructionPhaseControl";

type ConstructionPipelineRow = {
  date: string;
  [phase: string]: number | string;
};

type ConstructionPipelineDict = ConstructionPipelineRow[] | Record<string, any>;

type Props = {
  constructionPipeline: ConstructionPipelineDict | null | undefined;
  cloneDate: string | null;

  /** Canonical selected RAW phases (source of truth lives in parent) */
  phases: ConstructionPhase[];

  /** Notify parent with next RAW phase selection */
  onChangePhases: (phases: ConstructionPhase[]) => void;
};

// Phase groupings
const PHASE_GROUPS: Record<string, ConstructionPhase[]> = {
  Foundation: [
    "Plans",
    "Pad",
    "Footing",
    "Initial Backfill",
    "Plumbing Rough",
    "Slab",
    "Frame Delay",
  ],
  Framing: [
    "Walls & Joists",
    "Rafters & Cornice",
    "Decking",
    "Roofing",
    "Plumbing Top",
    "HVAC Rough",
    "Electrical Rough",
    "Frame Punch",
    "Insulation",
  ],
  Finishes: [
    "Sheetrock",
    "Wood Floors",
    "Tile",
    "Trim",
    "Paint",
    "Countertops",
    "Fixtures",
    "Final Clean",
    "Punchout",
    "Complete",
  ],
  Exterior: [
    "Brick",
    "Stone",
    "Flatwork",
    "Rough Grade",
    "Sprinkler",
    "TU Grade",
    "Sod",
    "Yard Complete",
  ],
};

// Keep your per-phase colors exactly as you already had them
const PHASE_COLORS: Record<string, string> = {
  Plans: "#8DD3C7",
  Pad: "#FFFFB3",
  Footing: "#BEBADA",
  "Initial Backfill": "#FB8072",
  "Plumbing Rough": "#80B1D3",
  Slab: "#FDB462",
  "Frame Delay": "#B3DE69",
  "Walls & Joists": "#FCCDE5",
  "Rafters & Cornice": "#D9D9D9",
  Decking: "#BC80BD",
  Roofing: "#CCEBC5",
  "Plumbing Top": "#FFED6F",
  "HVAC Rough": "#66C2A5",
  "Electrical Rough": "#FC8D62",
  "Frame Punch": "#8DA0CB",
  Insulation: "#E78AC3",
  Sheetrock: "#A6D854",
  "Wood Floors": "#FFD92F",
  Tile: "#E5C494",
  Trim: "#B3B3B3",
  Paint: "#1F78B4",
  Countertops: "#33A02C",
  Fixtures: "#FB9A99",
  "Final Clean": "#E31A1C",
  Punchout: "#FDBF6F",
  Complete: "#CAB2D6",
  "Pre-Inspections": "#6A3D9A",
  Brick: "#B15928",
  Stone: "#999999",
  Flatwork: "#CCCCCC",
  "Rough Grade": "#969696",
  Sprinkler: "#525252",
  "TU Grade": "#252525",
  Sod: "#006D2C",
  "Yard Complete": "#00441B",
};

// New group colors (4 distinct, high-contrast on dark backgrounds)
const GROUP_COLORS: Record<string, string> = {
  Foundation: "#4E79A7",
  Framing: "#F28E2B",
  Finishes: "#59A14F",
  Exterior: "#E15759",
};

const formatDate = (value: string) => {
  const parsed = new Date(value);
  return isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const truncateToSunday = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay(); // 0=Sunday
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

const toNumber = (v: any) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const isRowArray = (x: any): x is ConstructionPipelineRow[] =>
  Array.isArray(x) && (x.length === 0 || typeof x[0] === "object");

const normalizeConstructionPipeline = (
  cp: ConstructionPipelineDict | null | undefined
): ConstructionPipelineRow[] => {
  if (!cp) return [];
  if (isRowArray(cp)) return cp;

  const obj = cp as Record<string, any>;
  const keys = Object.keys(obj);
  if (!keys.length) return [];

  const dateCol = obj["date"];
  const sampleCol = dateCol ?? obj[keys[0]];

  const indices: (string | number)[] = (() => {
    if (Array.isArray(sampleCol)) return sampleCol.map((_: any, i: number) => i);
    if (sampleCol && typeof sampleCol === "object") return Object.keys(sampleCol);
    return [];
  })();

  if (!indices.length) return [];

  const rows: ConstructionPipelineRow[] = indices.map((idx) => {
    const row: any = {};
    for (const k of keys) {
      const col = obj[k];
      if (Array.isArray(col)) row[k] = col[Number(idx)];
      else if (col && typeof col === "object") row[k] = col[idx as any];
      else row[k] = col;
    }
    row.date = String(row.date ?? "");
    return row as ConstructionPipelineRow;
  });

  return rows.filter((r) => r.date);
};

const toWeekly = (rows: ConstructionPipelineRow[]) => {
  const sorted = [...rows].sort((a, b) => (a.date < b.date ? -1 : 1));
  const out: ConstructionPipelineRow[] = [];
  let curKey = "";
  let curRow: ConstructionPipelineRow | null = null;

  for (const r of sorted) {
    const d = new Date(r.date);
    if (isNaN(d.getTime())) continue;
    const weekKey = String(truncateToSunday(d));
    if (weekKey !== curKey) {
      if (curRow) out.push(curRow);
      curKey = weekKey;
      curRow = r;
    } else {
      curRow = r; // keep "last row in week"
    }
  }
  if (curRow) out.push(curRow);
  return out;
};

function trimEmptyEdges<T extends { date: string }>(rows: T[], seriesKeys: string[]): T[] {
  if (!rows.length) return rows;

  const hasAny = (r: T) => seriesKeys.some((k) => Number((r as any)[k] ?? 0) > 0);

  let start = 0;
  while (start < rows.length && !hasAny(rows[start])) start++;

  let end = rows.length - 1;
  while (end >= start && !hasAny(rows[end])) end--;

  return rows.slice(start, end + 1);
}

function buildJanuaryLabels(rows: Array<{ date: string }>): { labelDates: string[]; labelIndices: number[] } {
  const byYear: Record<string, { date: string; index: number }> = {};

  for (let i = 0; i < rows.length; i++) {
    const ds = rows[i].date;
    const d = new Date(ds);
    if (isNaN(d.getTime())) continue;
    if (d.getMonth() !== 0) continue; // January only

    const yearKey = String(d.getFullYear());
    if (!byYear[yearKey]) byYear[yearKey] = { date: ds, index: i };
  }

  const years = Object.keys(byYear)
    .map((y) => Number(y))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);

  const labelDates: string[] = [];
  const labelIndices: number[] = [];

  for (let j = 0; j < years.length; j++) {
    const key = String(years[j]);
    labelDates.push(byYear[key].date);
    labelIndices.push(byYear[key].index);
  }

  return { labelDates, labelIndices };
}

// Keep defaults here for "no parent selection yet" cases
const DEFAULT_SELECTED_PHASES: ConstructionPhase[] = [
  "Plans",
  "Pad",
  "Footing",
  "Initial Backfill",
  "Plumbing Rough",
  "Slab",
  "Frame Delay",
];

export const ConstructionPipelineChart: React.FC<Props> = React.memo(
  ({ constructionPipeline, cloneDate, phases, onChangePhases }) => {
    const [viewMode, setViewMode] = useState<"grouped" | "raw">("grouped");

    const rowsDaily = useMemo(
      () => normalizeConstructionPipeline(constructionPipeline),
      [constructionPipeline]
    );

    const weekly = useMemo(() => toWeekly(rowsDaily), [rowsDaily]);
    const hasData = useMemo(() => weekly.length > 0, [weekly]);
    const availableRawPhases = useMemo(() => {
      const keys = new Set<string>();
    
      const limit = Math.min(weekly.length, 500);
      for (let i = 0; i < limit; i++) {
        const r = weekly[i];
        for (const k of Object.keys(r || {})) {
          if (k !== "date") keys.add(k);
        }
      }

      keys.add("Plans");

      // keep canonical ordering from the selector component
      // (importing PHASE_OPTIONS directly would be nicer, but we keep it local)
      const ORDERED_ALL: ConstructionPhase[] = [
        "Plans",
        "Pad",
        "Footing",
        "Initial Backfill",
        "Plumbing Rough",
        "Slab",
        "Frame Delay",
        "Walls & Joists",
        "Rafters & Cornice",
        "Decking",
        "Roofing",
        "Plumbing Top",
        "HVAC Rough",
        "Electrical Rough",
        "Frame Punch",
        "Insulation",
        "Sheetrock",
        "Wood Floors",
        "Tile",
        "Trim",
        "Paint",
        "Countertops",
        "Fixtures",
        "Final Clean",
        "Punchout",
        "Complete",
        "Pre-Inspections",
        "Brick",
        "Stone",
        "Flatwork",
        "Rough Grade",
        "Sprinkler",
        "Sod",
        "TU Grade",
        "Yard Complete",
      ];
      return ORDERED_ALL.filter((p) => keys.has(p));
      // const present = ORDERED_ALL.filter((p) => keys.has(p));
      // return present;
    }, [weekly]);

    /**
     * ✅ Canonical selection is controlled by the parent (`phases` prop).
     * We sanitize it against what exists in the data.
     */
    const safeSelectedRaw = useMemo(() => {
      const allowed = new Set(availableRawPhases);

      const parentSelection = phases && phases.length ? phases : DEFAULT_SELECTED_PHASES;
      const cleaned = parentSelection.filter((p) => allowed.has(p));
      if (cleaned.length) return cleaned;

      const defaults = DEFAULT_SELECTED_PHASES.filter((p) => allowed.has(p));
      return defaults.length ? defaults : availableRawPhases;
    }, [phases, availableRawPhases]);

    // Build base chart rows (weekly) → raw or grouped
    const chartData = useMemo(() => {
      const rows = weekly;

      if (viewMode === "raw") {
        return rows.map((r) => {
          const out: any = { date: r.date };
          for (const p of safeSelectedRaw) out[p] = toNumber((r as any)[p]);
          return out;
        });
      }

      return rows.map((r) => {
        const out: any = { date: r.date };
        for (const [group, keys] of Object.entries(PHASE_GROUPS)) {
          const keysUsed = keys.filter((k) => safeSelectedRaw.includes(k));
          let s = 0;
          for (const k of keysUsed) s += toNumber((r as any)[k]);
          out[group] = s;
        }
        return out;
      });
    }, [weekly, viewMode, safeSelectedRaw]);

    const seriesKeys = useMemo(() => {
      if (viewMode === "raw") return safeSelectedRaw as string[];
      return Object.keys(PHASE_GROUPS);
    }, [viewMode, safeSelectedRaw]);

    const trimmedChartData = useMemo(() => {
      return trimEmptyEdges(chartData, seriesKeys);
    }, [chartData, seriesKeys]);

    const yMax = useMemo(() => {
      let best = 0;
      for (let i = 0; i < trimmedChartData.length; i++) {
        const r = trimmedChartData[i];
        const keys = Object.keys(r).filter((k) => k !== "date");
        const total = keys.reduce((acc, k) => acc + toNumber((r as any)[k]), 0);
        if (total > best) best = total;
      }
      return best;
    }, [trimmedChartData]);

    const { todayIndex, cloneIndex } = useMemo(() => {
      const cd = cloneDate ? new Date(cloneDate) : null;
      const targetClone = cd && !isNaN(cd.getTime()) ? truncateToSunday(cd) : null;
      const targetToday = truncateToSunday(new Date());

      let tIdx = -1;
      let cIdx = -1;

      let bestTodayDiff = Number.POSITIVE_INFINITY;
      let bestCloneDiff = Number.POSITIVE_INFINITY;

      for (let i = 0; i < trimmedChartData.length; i++) {
        const d = new Date(trimmedChartData[i].date);
        if (isNaN(d.getTime())) continue;
        const t = truncateToSunday(d);

        const diffToday = Math.abs(t - targetToday);
        if (diffToday < bestTodayDiff) {
          bestTodayDiff = diffToday;
          tIdx = i;
        }

        if (targetClone != null) {
          const diffClone = Math.abs(t - targetClone);
          if (diffClone < bestCloneDiff) {
            bestCloneDiff = diffClone;
            cIdx = i;
          }
        }
      }

      return { todayIndex: tIdx, cloneIndex: cIdx };
    }, [trimmedChartData, cloneDate]);

    const { labelDates, labelIndices } = useMemo(
      () => buildJanuaryLabels(trimmedChartData),
      [trimmedChartData]
    );

    const labelSkipStep = useMemo(() => {
      const width =
        typeof window !== "undefined" && window.innerWidth ? window.innerWidth : 1200;
      const maxLabels = Math.floor(width / 180);
      const labelCount = labelDates.length;
      return labelCount > maxLabels ? Math.ceil(labelCount / maxLabels) : 1;
    }, [labelDates.length]);

    const chartDataWithSpikes = useMemo(() => {
      return trimmedChartData.map((r, i) => ({
        ...r,
        today_spike: i === todayIndex ? yMax : 0,
        clone_spike: i === cloneIndex ? yMax : 0,
        jan_spike: labelIndices.indexOf(i) !== -1 ? yMax : 0,
      }));
    }, [trimmedChartData, todayIndex, cloneIndex, yMax, labelIndices]);

    const colorForKey = useMemo(() => {
      return (k: string) => {
        if (viewMode === "raw") return PHASE_COLORS[k] ?? "#8884d8";
        return GROUP_COLORS[k] ?? "#8884d8";
      };
    }, [viewMode]);

    const CustomTick = (props: any): React.ReactElement | null => {
      const { x, y, payload } = props;
      const idx = labelDates.indexOf(payload.value);
      if (idx === -1) return null;
      if (idx % labelSkipStep !== 0) return null;

      return (
        <text x={x} y={y + 30} textAnchor="middle" fontSize={20} fontWeight="bold" fill="white">
          {formatDate(payload.value)}
        </text>
      );
    };

    const CustomTooltip = ({ active, label, payload }: any) => {
      if (!active) return null;

      const rows = (payload || []).filter(
        (p: any) =>
          p?.dataKey !== "today_spike" && p?.dataKey !== "clone_spike" && p?.dataKey !== "jan_spike"
      );
      if (!rows.length) return null;

      const tooltipBg = "var(--card-bg)";
      const tooltipText = "var(--text-color)";
      const tooltipBorder = "var(--border-color)";

      const formatFullDate = (value: string) => {
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      };

      const sorted = [...rows].sort((a: any, b: any) => toNumber(b.value) - toNumber(a.value));

      return (
        <div
          style={{
            backgroundColor: tooltipBg,
            color: tooltipText,
            border: `1px solid ${tooltipBorder}`,
            borderRadius: 6,
            padding: "0.6rem 0.8rem",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          }}
        >
          <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 6 }}>
            {formatFullDate(String(label))}
          </div>

          {sorted.map((r: any, i: number) => (
            <div key={i} style={{ fontWeight: "bold", fontSize: 12 }}>
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  background: r.color,
                  marginRight: 6,
                }}
              />
              {r.name}: {Math.floor(toNumber(r.value))}
            </div>
          ))}
        </div>
      );
    };

    if (!hasData || !trimmedChartData.length) return null;

    return (
      <div className="w-full h-[1500px]">
        {/* ---------- TOP BAR (toggle + selector aligned) ---------- */}
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            alignItems: "flex-start",
            marginBottom: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.6rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontWeight: "bold" }}>Pipeline View:</span>

            <button
              onClick={() => setViewMode("grouped")}
              style={{
                padding: "0.35rem 0.6rem",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                opacity: viewMode === "grouped" ? 1 : 0.65,
              }}
            >
              Grouped
            </button>

            <button
              onClick={() => setViewMode("raw")}
              style={{
                padding: "0.35rem 0.6rem",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                opacity: viewMode === "raw" ? 1 : 0.65,
              }}
            >
              Raw phases
            </button>

            <span style={{ opacity: 0.7, fontSize: "0.9rem" }}>
              (Weekly downsampled) • showing {viewMode === "raw" ? safeSelectedRaw.length : "grouped"}
            </span>
          </div>

          {/* ✅ Selector is controlled by parent via phases/onChangePhases */}
          <div style={{ flex: "1 1 520px", minWidth: 320 }}>
            <ConstructionPhaseControl
              mode={viewMode}
              value={safeSelectedRaw}
              onChange={onChangePhases}
              phaseOptions={availableRawPhases}
              phaseGroups={PHASE_GROUPS}
            />
          </div>
        </div>

        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartDataWithSpikes} margin={{ top: 30, right: 50, left: 10, bottom: 40 }}>
            <XAxis
              dataKey="date"
              type="category"
              allowDuplicatedCategory={false}
              tickFormatter={formatDate}
              interval={0}
              tick={<CustomTick />}
              height={100}
              minTickGap={0}
            />

            <YAxis
              domain={[0, yMax]}
              allowDecimals={false}
              tick={{ fontSize: 18, fontWeight: "bold", fill: "white", dx: -10 }}
            />

            <Legend
              verticalAlign="top"
              content={({ payload }: any) => {
                const filtered = (payload || []).filter((p: any) => {
                  const k = String(p?.dataKey ?? "");
                  // Hide helper spike series
                  if (k.endsWith("_spike")) return false;

                  // Recharts sometimes puts bogus entries in payload
                  if (!p?.value) return false;

                  return true;
                });

                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-evenly",
                      alignItems: "center",
                      flexWrap: "wrap",
                      width: "100%",
                      fontSize: "20px",
                      fontWeight: "bold",
                      paddingBottom: "1rem",
                    }}
                  >
                    {filtered.map((entry: any, index: number) => (
                      <div
                        key={`item-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                          minWidth: "150px",
                        }}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: entry.color, // ✅ uses your Area/Line stroke/fill
                          }}
                        />
                        <span>{entry.value}</span> {/* ✅ uses your "name" (or dataKey) */}
                      </div>
                    ))}
                  </div>
                );
              }}
            />

            {seriesKeys.map((k) => (
              <Area
                key={k}
                dataKey={k}
                stackId="a"
                type="monotone"
                isAnimationActive={false}
                fillOpacity={1}
                name={k}
                stroke={colorForKey(k)}
                fill={colorForKey(k)}
              />
            ))}

            <Line
              type="stepAfter"
              dataKey="today_spike"
              name="Today"
              stroke="#9AA0A6"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              activeDot={false}
            />

            <Line
              type="stepAfter"
              dataKey="clone_spike"
              stroke="#000000"
              strokeDasharray="4"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              legendType="none"
              activeDot={false}
            />

            <Line
              type="stepAfter"
              dataKey="jan_spike"
              stroke="#ffffffff"
              strokeDasharray={4}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              legendType="none"
              activeDot={false}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "#ffffff",
                strokeWidth: 2,
                strokeDasharray: "4 4",
                opacity: 0.8,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }
);